/*
 * customsirge_atc_price.js
 * Keeps the Add-to-Cart button's price — and, when enabled, the Kaching
 * subscription/bundle option prices — in sync with the chosen quantity, the
 * selected Kaching plan, and the quantity-based automatic discounts.
 *
 * The quantity discounts come from the Quantity-tiles block (tile quantity + its
 * badge), serialized onto the <product-form> as data-qty-discounts, e.g.
 * "1=;2=-10%;3=-15%". The badge percent is parsed here; the discount for a
 * quantity is the badge of the HIGHEST tile whose quantity <= the current
 * quantity (so a "3+" tier extends to 4, 5, …).
 *
 * Kaching option prices ALREADY reflect the chosen quantity (the widget reads it),
 * so the only thing to layer on is the automatic quantity discount:
 *   - buy-once          : theme per-unit base × qty × (1 - discount)
 *   - subscription plan : selected Kaching price (already the qty total) × (1 - discount),
 *                         discounted only when the quantity discount applies to subs.
 *
 * When enabled, every Kaching option price matched by data-sub-block-price-selector
 * is rewritten to price × (1 - discount) — NOT × qty, the widget already did that.
 * Tracking is keyed off the price's TEXT NODE (WeakMaps), not the element, so
 * overlapping/nested selectors — and the separate selected-price read used for the
 * ATC — share one original and never compound. Kaching re-renders create fresh text
 * nodes that are re-adopted automatically. Writes are change-guarded so our own edits
 * don't drive the MutationObserver into a loop.
 */
(function () {
  // Value of the LAST money-looking number in a string (so a struck compare-at like
  // "$39.00 $33.15" resolves to the final 33.15).
  function lastNumber(text) {
    if (!text) return null;
    var re = /\d[\d,]*(?:\.\d+)?/g;
    var m;
    var last = null;
    while ((m = re.exec(text)) !== null) last = m;
    if (!last) return null;
    var v = parseFloat(last[0].replace(/,/g, ''));
    return isNaN(v) ? null : v;
  }

  function moneyToCents(text) {
    var v = lastNumber(text);
    return v == null ? null : Math.round(v * 100);
  }

  // "1=;2=-10%;3=-15%" -> [{q:1,pct:0},{q:2,pct:10},{q:3,pct:15}] ascending by qty.
  function parseSchedule(str) {
    var out = [];
    (str || '').split(';').forEach(function (pair) {
      pair = pair.trim();
      if (!pair) return;
      var eq = pair.indexOf('=');
      if (eq < 0) return;
      var q = parseInt(pair.slice(0, eq), 10);
      if (!(q > 0)) return;
      var badge = pair.slice(eq + 1);
      var m = badge.match(/(\d+(?:\.\d+)?)\s*%/);
      out.push({ q: q, pct: m ? parseFloat(m[1]) : 0 });
    });
    out.sort(function (a, b) {
      return a.q - b.q;
    });
    return out;
  }

  function discountPct(schedule, qty) {
    var pct = 0;
    for (var i = 0; i < schedule.length; i++) {
      if (schedule[i].q <= qty) pct = schedule[i].pct;
      else break;
    }
    return pct;
  }

  // Per-UNIT discounted price in cents, rounded UP to the cent. Shopify applies the
  // automatic discount to each unit and rounds the per-unit price up (observed at cart:
  // 20.50 × 0.85 = 17.425 -> 17.43), THEN multiplies by quantity — so a qty-total must
  // be reduced to its per-unit before discounting, not discounted whole (which would
  // give 52.28 instead of 52.29). The tiny epsilon stops a float-inflated exact value
  // from being pushed to the next cent.
  function ceilUnit(unitCents, pct) {
    if (!pct) return unitCents;
    return Math.ceil((unitCents * (100 - pct)) / 100 - 1e-9);
  }

  function setup(form) {
    var priceEl = form.querySelector('.customsirge-quantity-and-buy__atc-price');
    if (!priceEl) return; // unavailable variant renders no price element
    var qtyInput = form.querySelector('.customsirge-quantity-and-buy__input');

    var baseUnit = parseInt(form.getAttribute('data-base-unit-price'), 10);
    if (isNaN(baseUnit)) baseUnit = 0;
    var currency = (form.getAttribute('data-currency') || 'USD').trim() || 'USD';
    var selSelected = (form.getAttribute('data-sub-price-selector') || '').trim();
    var selBlock = (form.getAttribute('data-sub-block-price-selector') || '').trim();
    var subsGetDiscount = form.getAttribute('data-qty-discount-subs') === 'true';
    var schedule = parseSchedule(form.getAttribute('data-qty-discounts'));

    var fmt;
    try {
      fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
    }

    // Per price TEXT NODE: its original (pre-discount) value, and the last value we wrote.
    var origVal = new WeakMap();
    var outVal = new WeakMap();

    function qty() {
      var q = parseInt(qtyInput && qtyInput.value, 10);
      return q > 0 ? q : 1;
    }

    // Deepest/last text node holding a digit inside el (so a symbol/amount split or a
    // nested price span is handled, and two overlapping selectors resolve to one node).
    function numberNode(el) {
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var node;
      var target = null;
      while ((node = walker.nextNode())) {
        if (/\d/.test(node.nodeValue)) target = node;
      }
      return target;
    }

    // The tracked original text for a price node: keep our stored original while the
    // node still shows our last output; otherwise the node is fresh from Kaching (its
    // own render, which already reflects the quantity), so adopt it as the new original.
    function originalOf(t) {
      var out = outVal.get(t);
      if (!(out !== undefined && t.nodeValue === out)) origVal.set(t, t.nodeValue);
      return origVal.get(t);
    }

    // Tracked original (pre-discount) cents of the price inside el — this is the Kaching
    // price, which already reflects the quantity. Null if none.
    function trackedCents(el) {
      var t = numberNode(el);
      if (!t) return null;
      return moneyToCents(originalOf(t));
    }

    // Rewrite each matched Kaching option price to its discounted qty-total. The widget
    // price is already the qty-total, so reduce it to the per-unit (÷ qty), discount the
    // per-unit UP, then × qty — matching how Shopify rounds at cart. De-duplicated per
    // text node.
    function rewriteBlockPrices(pct, q) {
      if (!subsGetDiscount || !selBlock) return;
      var list;
      try {
        list = document.querySelectorAll(selBlock);
      } catch (e) {
        return; // invalid selector
      }
      var seen = [];
      Array.prototype.forEach.call(list, function (el) {
        var t = numberNode(el);
        if (!t || seen.indexOf(t) !== -1) return;
        seen.push(t);
        var original = originalOf(t);
        var cents = moneyToCents(original);
        if (cents == null) return;
        var discounted = ceilUnit(Math.round(cents / q), pct) * q;
        var next = original.replace(/\d[\d,]*(?:\.\d+)?(?![\s\S]*\d)/, (discounted / 100).toFixed(2));
        if (t.nodeValue !== next) t.nodeValue = next;
        outVal.set(t, next);
      });
    }

    // The selected Kaching plan's price (already the quantity total), or null.
    function selectedPriceCents() {
      if (!selSelected) return null;
      var el;
      try {
        el = document.querySelector(selSelected);
      } catch (e) {
        return null; // invalid selector
      }
      return el ? trackedCents(el) : null;
    }

    function update() {
      var q = qty();
      var pct = discountPct(schedule, q);

      // 1) Discount the Kaching option prices first, so a selected price shared with
      //    the block read has its original established.
      rewriteBlockPrices(pct, q);

      // 2) ATC total. Discount the PER-UNIT price up, then × qty (matches cart rounding).
      var selPrice = selectedPriceCents(); // selected Kaching price (already qty total), or null
      var totalCents;
      if (selPrice != null) {
        // subscription / Kaching-priced plan: reduce qty-total to per-unit, discount, × qty
        var subPct = subsGetDiscount ? pct : 0;
        totalCents = ceilUnit(Math.round(selPrice / q), subPct) * q;
      } else {
        // buy-once: theme per-unit base, discount, × qty
        totalCents = ceilUnit(baseUnit, pct) * q;
      }
      var str = '— ' + fmt.format(totalCents / 100);
      if (priceEl.textContent !== str) priceEl.textContent = str;
    }

    update();

    if (qtyInput) {
      qtyInput.addEventListener('change', update);
      qtyInput.addEventListener('input', update);
    }

    // Watch the product info column so the Kaching widget re-triggers the total when it
    // updates a price OR toggles the selected class, plus when the quantity tiles fire.
    // rAF-debounced; writes are change-guarded so our own edits don't loop.
    var scope = form.closest('.customsirge-main-product__info') || form.parentNode || document.body;
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        update();
      });
    });
    observer.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Belt-and-suspenders: a click inside the info column (e.g. a subscription radio)
    // may update the widget price a tick later.
    scope.addEventListener(
      'click',
      function () {
        setTimeout(update, 60);
      },
      true,
    );
  }

  function init() {
    document.querySelectorAll('product-form.customsirge-quantity-and-buy').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
